(ns frontend.view.entity-viewers.modules.fees
  (:require
   [frontend.utils :as fu]
   [frontend.state.current-session :as session]
   [reagent.core :as r]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [frontend.actions.entity :as entity-actions]
   [frontend.view.modals.events :as modals]
   [common.localization :as loc]
   [basic-components.html-table.table :as table]
   [frontend.view.entity :as entity-view]
   [potpuri.core :as potpuri :refer [index-by fn-> fn->> dissoc-in]]
   [frontend.view.modals.delete-confirmation.view :as delete-confirmation]
   [frontend.view.modals.entity-editor.view :as entity-editor-modal]
   [frontend.logging :as logging]
   [common.currencies :as currencies]
   [clojure.string :as string]
   [common.schema.registry :as registry]
   [common.schema.entity-type-names :as entity-type-names]
   [basic-components.html-table.cell :as table-cell]
   [frontend.routing :as routing]
   [common.entity :as entity]))

(defn edit-fee
  [{:keys [row-data]}]
  (modals/open-modal!
   entity-editor-modal/modal-key
   {:entity-id         (:db/id row-data)
    :skip-success-msg? true
    :title-text        (loc/tr :edit-fee)
    :on-processed!     (fn [{:keys [new-entity-id close-cb!]}]
                         (js/setTimeout (fn []
                                          (close-cb!))
                                        2000))}))
(defn delete-fee
  [{:keys [row-data]}]
  (let [entity-id (:db/id row-data)]
    (modals/open-modal! delete-confirmation/modal-key
                        {:body       (loc/tr :delete-entity [(loc/tr (:entity/type row-data))])
                         :on-delete! (fn [_]
                                       (entity-actions/*->disable!
                                        {:db/id entity-id}
                                        (fn []
                                          (logging/info "Successfully deleted a fee"))
                                        (fn []
                                          (logging/error "Couldn't delete a fee"))))})))

(defn tools-cell-renderer
  [row-data]
  [:div
   {:class "flex justify-end"}
   [dropdown-menu/view
    {:trigger [:i.fas.fa-ellipsis-v]
     :items   [{:value :edit-fee
                :label     (loc/tr :edit-fee)
                :icon-class     "fas fa-pen"
                :on-click #(edit-fee row-data)}
               {:value :remove
                :label     (loc/tr :remove)
                :icon-class    "fas fa-trash-alt"
                :on-click #(delete-fee row-data)}]}]])

(defn currency-custom-renderer
  [{:keys [row-data]}]
  [:div
   [:span
    (some-> (:fee/cur-amount row-data)
            fu/bigdec->float
            fu/format-with-separators)]
   [:span (str " " (currencies/get-label (:fee/cur-amount--sym row-data)))]])

(defn default-table-first-columns
  []
  [{:column/key           :fee/cur-amount
    :column/label         (loc/tr :fee/cur-amount)
    :column/data-type     :data.type/currency-value
    :column/cell-renderer currency-custom-renderer
    :column/sortable?     true}
   {:column/key        :fee/note
    :column/label      (loc/tr :fee/note)
    :column/data-type  :data.type/text-multiline
    :column/class-name "max-width"
    :column/tooltip?   true}])

(defn default-table-last-column
  []
  (when-not (session/is-read-only-user?)
    [{:column/key           :tools
      :column/header-renderer (fn [_] "")
      :column/cell-renderer tools-cell-renderer
      :column/class-name    "text-center tools-col"}]))

;;TODO: probably should be in frontend.utils, but for now it causes circular dependency if we place it there,
;; so for now it's only fee module specific code
(defn display-related-entities-as-links
  [entity-ids]
  (let [[first-related & rest-related]
        (->> entity-ids
             (keep (fn [eid]
                     (when-let [entity (session/get-entity eid)]
                       {:display-string (entity/display-string entity :short session/get-entity)
                        :db/id (:db/id entity)})))
             (sort-by :display-string))]
    [:div
     [:span
      [:a
       {:class ""
        :on-click #(routing/open-entity (:db/id first-related))}
       (:display-string first-related)]]
     (->> rest-related
          (map (fn [related]
                 ^{:key (str "related-entity-" (:db/id related))}
                 [:span
                  ", "
                  [:a
                   {:on-click #(routing/open-entity (:db/id related))}
                   (:display-string related)]]))
          (doall))]))

(defn associated-entities-custom-renderer
  [attribute field-schema {:keys [row-data]}]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        value (fu/get-value tenant tenant-schema row-data attribute)]
    [:div
     (some->> value
              display-related-entities-as-links)]))

(defn associated-entities-columns
  [fees-associated-entities-attributes]
  (let [tenant-schema @session/tenant-schema]
    (->> fees-associated-entities-attributes
         (map (fn [attribute]
                (let [field-schema (registry/get-field-schema tenant-schema attribute)
                      entity-type (->> field-schema
                                       :field/entity-types
                                       (first))]
                  {:column/key           attribute
                   :column/label         (loc/tr :linked-associations
                                             [(string/lower-case (entity-type-names/get-plural
                                                                  entity-type
                                                                  :en))])
                   :column/data-type     :data.type/text
                   :column/cell-renderer (partial associated-entities-custom-renderer
                                                  attribute
                                                  field-schema)
                   :column/tooltip?      true}))))))

(defn default-table-columns
  [fees-associated-entities-attributes]
  (concat (default-table-first-columns)
          (associated-entities-columns fees-associated-entities-attributes)
          (default-table-last-column)))

(defn resolve-row-data
  [fees]
  (->> fees
       (map (fn [fee]
              (merge fee {:trunc-limit 50})))))

(defn module-content
  [fees fees-associated-entities-attributes]
  (let [table-state (r/atom {})]
    (fn [fees fees-associated-entities-attributes]
      (let [resolved-fees (resolve-row-data fees)]
        [table/view resolved-fees
         {:id-key       :db/id
          :table-key    "fees"
          :class-name   "w-100"
          :state        table-state
          :group-by-key :fee/status
          :columns      (default-table-columns fees-associated-entities-attributes)
          :settings     {:show-selection?     true
                         :show-filters?       true
                         :show-grouping?      false
                         :show-sorting?       true
                         :default-sort-key    :common/created-on
                         :column-order        [:db/id :entity/type :common/created-on]
                         :sticky-column-group 0}}]))))

(defn add-new-fee-button
  [entity]
  [:div
   (when-not (session/is-read-only-user?)
     [fu/tooltip-overlay
      {:id (str "add-fee-" (:db/id entity))}
      (loc/tr :add-fee)
      [:i
       {:class-name (fu/cs "fal clickable fa-plus-circle")
        :on-click   (fn [_]
                      (entity-view/open-creator! :entity.type/fee
                                                 {:start-value {:common/related-entities
                                                                #{(:db/id entity)}}}))}]])])

(defn modules
  [{:keys [save-module-collapsed! module-collapsed-path entity-id
           fees-associated-entities-attributes]}]
  (let [entity @(r/track session/get-entity entity-id)
        fees (->> @session/all-fees
                  (filter (fn [fee]
                            (some #{entity-id} (:common/related-entities fee)))))
        module-id (keyword "fees" (name (:entity/type entity)))]
    [{:module-id        module-id
      :header           (loc/tr :fees)
      :header-tools     (add-new-fee-button entity)
      :collapsed?       @(r/track session/get-view-config (module-collapsed-path module-id))
      :start-collapsed? (empty? fees)
      :save-module-collapsed! (partial save-module-collapsed! (:entity/type entity))
      :content          [:div
                         (if (empty? fees)
                           [:h2
                             {:class "mb-0 px-1 text-base-light-text-paler text-sm"}
                            (if (session/is-read-only-user?)
                              (loc/tr :no-fees-logged-read-only)
                              (loc/tr :no-fees-logged))]
                           [:div
                            [module-content fees fees-associated-entities-attributes]])]}]))
